<template>
  <card card-body-classes="table-full-width">
    <edit-season-ticket-revision :show="showModal" :form-details="modalFormDetails"
                                 :static-details="modalStaticDetails"
                                 @close="showModal=false"/>
    <h4 slot="header" class="card-title">{{ $t('seasonTicketRevisions.tableTitle') }}</h4>
    <div>
      <div
        class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
      >
        <el-select
          class="select-primary mb-3 pagination-select"
          v-model="pagination.perPage"
          placeholder="Per page"
        >
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
          >
          </el-option>
        </el-select>

        <div class="row">
          <div class="col-6">
            <base-input>
              <el-input
                type="search"
                class="mb-3 search-input"
                prefix-icon="el-icon-search"
                :placeholder="$t('generic.searchInTablePlaceholder')"
                v-model="searchQuery"
                aria-controls="datatables"
              >
              </el-input>
            </base-input>
          </div>
          <div class="col-6" v-if="$auth.can({season_ticket: 'create'}, 'permissions')">
            <create-season-ticket-revision :season-ticket-id="seasonTicketId"/>
          </div>
        </div>
      </div>

      <el-table :data="queriedData" :empty-text="$t('generic.noData')">
        <el-table-column type="expand">
          <template slot-scope="props">
            <season-ticket-revision-details :details="props.row"/>
          </template>
        </el-table-column>
        <el-table-column
          v-for="column in tableColumns"
          :key="column.label"
          :min-width="column.minWidth"
          :prop="column.prop"
          :label="$t(column.label)"
        >
        </el-table-column>
        <!-- Created -->
        <el-table-column :label="$t('seasonTicketRevisions.table.on_sale')">
          <div slot-scope="{ row }">
            {{ row.sale_starts_at | time }}
            <span v-if="row.sale_ends_at"> - {{ row.sale_ends_at | time }}</span>
          </div>
        </el-table-column>
        <el-table-column :label="$t('seasonTicketRevisions.details.price')">
          <div slot-scope="{ row }">
            {{ row.price | money }}
          </div>
        </el-table-column>
        <el-table-column :label="$t('generic.updated')">
          <div slot-scope="{ row }">
            <!-- TODO: translate time -->
            {{ row.updated_at | time }}
          </div>
        </el-table-column>
        <el-table-column :min-width="135" align="right"
                         v-if="$auth.can({fare: 'update'}, 'permissions') || $auth.can({fare: 'delete'}, 'permissions')">
          <div slot-scope="props">
            <base-button
              v-if="$auth.can({fare: 'update'}, 'permissions')"
              @click.native="handleEdit(props.$index, props.row)"
              class="mr-2"
              type="info"
              size="sm"
              :disabled="props.row.status == 'approved'"
              icon
              round
              simple>
              <i class="tim-icons icon-pencil"></i>
            </base-button>
            <base-button
              v-if="$auth.can({fare: 'update'}, 'permissions')"
              @click.native="handleStatusChange(props.$index, props.row)"
              class="mr-2"
              type="warning"
              size="sm"
              icon
              round
              simple>
              <i class="tim-icons icon-lock-circle"></i>
            </base-button>
            <base-button
              v-if="$auth.can({season_ticket_revision: 'delete'}, 'permissions')"
              @click.native="handleDelete(props.$index, props.row)"
              class=""
              type="danger"
              size="sm"
              :disabled="props.row.status == 'approved'"
              icon
              round
              simple>
              <i class="tim-icons icon-simple-remove"></i>
            </base-button>
          </div>
        </el-table-column>
      </el-table>
    </div>
    <div
      slot="footer"
      class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
    >
      <div class="">
        <p class="card-category">
          {{ $t('generic.tablePaginationSummary', {from: from + 1, to: to, total: total}) }}
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      >
      </base-pagination>
    </div>
  </card>
</template>
<script>
import {Option, Select, Table, TableColumn} from 'element-ui';
import {BaseAlert, BasePagination} from 'src/components';
import Fuse from 'fuse.js';
import {mapActions, mapState} from "vuex";
import CreateSeasonTicketRevision from "./CreateSeasonTicketRevision";
import EditSeasonTicketRevision from "./EditSeasonTicketRevision";
import SeasonTicketRevisionDetails from "./SeasonTicketRevision/SeasonTicketRevisionDetails";
import swal from "sweetalert2";

export default {
  name: 'SeasonTicketRevisionsTable',
  components: {
    BasePagination,
    BaseAlert,
    CreateSeasonTicketRevision,
    EditSeasonTicketRevision,
    SeasonTicketRevisionDetails,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn
  },
  computed: {
    ...mapState({
      error: state => state.season_ticket_revisions.error,
    }),
    /***
     * Returns a page from the searched data or the whole data. Search is performed in the watch section below
     */
    queriedData() {
      let result = this.tableData;
      if (this.searchedData.length > 0) {
        result = this.searchedData;
      }
      return result.slice(this.from, this.to);
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.searchedData.length > 0
        ? this.searchedData.length
        : this.tableData.length;
    }
  },
  props: {
    seasonTicketId: {
      type: String,
      required: true,
    },
    seasonTicket: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      pagination: {
        perPage: 50,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50],
        total: 0
      },
      searchQuery: '',
      propsToSearch: ['status'],
      tableColumns: [
        {
          prop: 'revision',
          label: 'seasonTicketRevisions.details.revision',
        },
        {
          prop: 'place_of_purchase',
          label: 'seasonTicketRevisions.details.place_of_purchase',
        },
        {
          prop: 'status',
          label: 'generic.status',
        },
      ],
      tableData: [],
      searchedData: [],
      fuseSearch: null,
      showModal: false,
      modalFormDetails: {},
      modalStaticDetails: {},
    };
  },
  methods: {
    ...mapActions(['DeleteSeasonTicketRevision', 'UpdateSeasonTicketRevision', 'GetSeasonTicket']),
    handleEdit(index, row) {

      let additionalData = []
      if (row.requires_additional_details) {
        if (row.requires_additional_details.personal_data) {
          additionalData = additionalData.concat("personal_data")
        }
        if (row.requires_additional_details.contacts) {
          additionalData = additionalData.concat("contacts")
        }
        if (row.requires_additional_details.assignee) {
          additionalData = additionalData.concat("assignee")
        }
        if (row.requires_additional_details.document) {
          additionalData = additionalData.concat("document")
        }
      }

      this.modalFormDetails = {
        duration: row.duration,
        id: row.id,
        max_start_postpone_days: row.max_start_postpone_days,
        metadata: row.metadata,
        modified_by: row.modified_by,
        place_of_purchase: row.place_of_purchase,
        price: row.price / 100,
        revision: row.revision,
        sale_starts_at: row.sale_starts_at,
        sale_ends_at: row.sale_ends_at,
        season_ticket_id: row.season_ticket_id,
        status: row.status,
        additionalData: additionalData
      };
      this.modalStaticDetails = row;
      this.showModal = true;
    },
    handleDelete(index, row) {
      swal({
        title: this.$t('seasonTicketRevisions.delete.text'),
        text: `'${row.revision}'`,
        confirmButtonText: this.$t('seasonTicketRevisions.delete.actionBtnLabel'),
        cancelButtonText: this.$t('seasonTicketRevisions.delete.cancelBtnLabel'),
        buttonsStyling: false,
        confirmButtonClass: 'btn btn-danger',
        cancelButtonClass: 'btn btn-secondary',
        type: 'question',
        showCancelButton: true
      }).then(result => {
        if (result.value) {
          const oldDetails = row
          this.DeleteSeasonTicketRevision({seasonTicketId: this.seasonTicket.id, id: row.id}).then(() => {
            if (this.error == null) {
              this.$notify({
                type: 'success',
                message: this.$t('seasonTicketRevisions.delete.successNotification', oldDetails),
                icon: 'tim-icons icon-alert-circle-exc'
              })
              this.GetSeasonTicket(this.$route.params.id)
            }

          })
        }
      });
    },
    handleStatusChange(index, row) {
      let newStatus = 'approved';
      if (row.status === newStatus) {
        newStatus = 'not_approved';
      }
      swal({
        title: this.$t('seasonTicketRevisions.statusChange.text'),
        text: `'${newStatus}'`,
        confirmButtonText: this.$t('seasonTicketRevisions.statusChange.actionBtnLabel'),
        cancelButtonText: this.$t('seasonTicketRevisions.statusChange.cancelBtnLabel'),
        buttonsStyling: false,
        confirmButtonClass: 'btn btn-danger',
        cancelButtonClass: 'btn btn-secondary',
        type: 'question',
        showCancelButton: true
      }).then(result => {
        if (result.value) {
          this.UpdateSeasonTicketRevision({
            seasonTicketId: this.seasonTicket.id,
            id: row.id,
            data: {status: newStatus}
          }).then(() => {
            if (this.error == null) {
              this.$notify({
                type: 'success',
                message: this.$t('seasonTicketRevisions.statusChange.successNotification', {
                  status: newStatus
                }),
                icon: 'tim-icons icon-alert-circle-exc'
              })
            }
            this.GetSeasonTicket(this.$route.params.id)
          })
        }
      });
    },
  },
  mounted() {
  },
  watch: {
    /**
     * Searches through the table data by a given query.
     * NOTE: If you have a lot of data, it's recommended to do the search on the Server Side and only display the results here.
     * @param value of the query
     */
    searchQuery(value) {
      let result = this.tableData;
      if (value !== '') {
        result = this.fuseSearch.search(this.searchQuery);
      }
      this.searchedData = result;
    },
    "seasonTicket"(details) {
      let data = []
      if (details && details.revisions) {
        data = details.revisions;
      }
      this.tableData = data
      // Fuse search initialization.
      this.fuseSearch = new Fuse(this.tableData, {
        keys: this.propsToSearch,
        threshold: 0.3
      });
    }
  }
};
</script>
<style scoped>
</style>
